import React, { useEffect, useMemo, useState, KeyboardEventHandler, ClipboardEvent } from 'react'
import { AddDomainInputStyle } from 'Components/Elements/AddDomainModal/SingleDomain/AddDomainModal.styled'
import { useTranslation } from 'react-i18next'
import CreatableSelect from 'react-select/creatable'
import { SelectOption } from 'Types/Option'
import { validateDomain } from 'Utils/validation'
import { InputError, InputContainer } from './MultipleDomainInput.styled'
import { MAX_DOMAINS } from '../NewMultipleDomains/helpers'

const components = {
  DropdownIndicator: null,
}

export const OVERPOPULATED_PARAMETER = 'isOverpopulated'
export const ADD_DOMAIN_REPLACE_REGEX = /\/+$/

interface OverpopulatedMultiDomain extends SelectOption {
  [OVERPOPULATED_PARAMETER]?: boolean
}
interface MultipleDomainInputProps {
  addDomain: (domain: string) => void
  setDomains: (domains: SelectOption[]) => void
  domains: SelectOption[]
  isClearable?: boolean
}

export const MultipleDomainInput = ({
  addDomain,
  setDomains,
  domains,
  isClearable = true,
}: MultipleDomainInputProps) => {
  const { t } = useTranslation('domainManagement')

  const [inputValue, setInputValue] = useState('')
  const [inputError, setInputError] = useState<string>('')

  const domainsValue: OverpopulatedMultiDomain[] = useMemo(
    () =>
      domains.map((domain, index) => ({
        ...domain,
        [OVERPOPULATED_PARAMETER]: index >= MAX_DOMAINS,
      })),
    [domains]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => handleOverpopulationErrors(), [domainsValue])

  const handleKeyDown: KeyboardEventHandler = (event) => {
    if (!inputValue) return
    switch (event.key) {
      case ' ':
      case ',':
      case 'Tab':
      case 'Enter':
        handelAddDomain()
        event.preventDefault()
    }
  }

  const handelAddDomain = () => {
    if (!inputValue) {
      setInputError('')
    } else if (validateDomain(inputValue)) {
      addDomain(inputValue.replace(ADD_DOMAIN_REPLACE_REGEX, ''))
      setInputValue('')
      setInputError('')
    } else {
      setInputError(t('add_new_domain_modal.invalid_domain'))
    }
  }

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const inputValue: string = event?.clipboardData?.getData('text/plain') || ''
    if (!inputValue) return setInputError('')
    const validDomains = inputValue
      .split(',')
      .map((domain) => domain.trim().replace(ADD_DOMAIN_REPLACE_REGEX, ''))
      .filter((domain) => validateDomain(domain))
      .join(',')

    addDomain(validDomains)
    setTimeout(() => setInputValue(''), 10)
    setInputError('')
  }

  const handleChange = (newValue: typeof domainsValue) => {
    if (!newValue) return

    const domains: SelectOption[] = newValue.map((domain) => {
      delete domain[OVERPOPULATED_PARAMETER]
      return domain
    })
    setDomains(domains)
  }

  const handleOverpopulationErrors = () => {
    const overpopulatedCount = domainsValue.filter(
      (domain) => domain[OVERPOPULATED_PARAMETER]
    ).length
    if (overpopulatedCount)
      setInputError(
        t(
          `add_new_multiple_domains_modal.errors.${
            overpopulatedCount === 1 ? 'one' : 'multi'
          }_overpopulated`,
          { number: overpopulatedCount }
        )
      )
  }
  return (
    <InputContainer onPaste={handlePaste}>
      <CreatableSelect
        styles={AddDomainInputStyle}
        components={components}
        inputValue={inputValue}
        isClearable={isClearable}
        isMulti
        menuIsOpen={false}
        onMenuClose={handelAddDomain}
        onChange={(newValue) => handleChange(newValue as typeof domainsValue)}
        onInputChange={(newValue) => setInputValue(newValue)}
        onKeyDown={handleKeyDown}
        placeholder={t('add_new_domain_modal.example')}
        value={domainsValue}
      />
      {!!inputError.length && <InputError>{inputError}</InputError>}
    </InputContainer>
  )
}
